<!-- eslint-disable vue/no-v-html -->
<template>
  <div :class="`relative @container text-foreground ${hasFocus ? 'z-100' : ''}`">
    <div v-if="hasFocus" class="fixed -z-10 inset-0 bg-black/50" @click="close"></div>
    <div
      :class="`bg-surface px-6 ${hasFocus ? (query ? 'rounded-t' : 'rounded outline outline-border') : 'rounded outline outline-border'}`"
      @focusin="hasFocus = true">
      <div class="flex items-center gap-3">
        <input
          v-model="query"
          type="text"
          maxlength="75"
          autocorrect="off"
          spellcheck="false"
          autocomplete="off"
          :aria-label="t('Search')"
          :placeholder="t('Find your next event here')"
          class="h-12 w-full bg-transparent border-none rounded-none outline-none ring-0 focus:ring-0 focus:outline-none font-inherit leading-inherit appearance-none placeholder:text-muted-foreground p-0" />
        <CancelIcon v-if="query" class="size-4 cursor-pointer text-muted-foreground flex-none" @click="close" />
        <SearchIcon class="size-6 ml-auto text-muted-foreground flex-none" />
      </div>
      <div
        v-show="query && hasFocus"
        ref="resultsWrapperElement"
        class="absolute bg-surface w-full search-results inset-x-0 px-6 pb-6 rounded-b flex flex-col gap-3">
        <template v-if="!isLoading">
          <template v-if="otherResults.length">
            <hr v-if="events.length" class="@xl:my-3" />
            <RouterLink v-for="result in otherResults" :key="result.to" :to="result.to" class="flex gap-1 items-center">
              <component :is="result.icon" class="size-4 opacity-50" />
              <span class="me-1" v-html="highlightQuery(result.name)" />
              <span v-if="isTeamResult(result)" class="bg-background text-xs font-medium me-1 px-2.5 py-0.5 rounded-sm">
                {{ result.sportType }}
              </span>
              <span class="bg-background text-xs font-medium me-1 px-2.5 py-0.5 rounded-sm">{{ result.type }}</span>
            </RouterLink>
          </template>
          <hr class="" />
          <template v-if="events.length">
            <span class="text-sm text-muted-foreground font-bold @xl:my-3">{{ t("Upcoming events") }}</span>
            <template v-for="(event, i) in events" :key="event.eventId">
              <EventCard :event="camelToSnake(event)" class="p-0! shadow-none" small />
              <hr v-if="i < events.length - 1" class="@xl:my-3" />
            </template>
          </template>
          <template v-if="!otherResults.length && !events.length">{{ $t("No results found") }}</template>
        </template>
        <template v-else>
          <div class="flex flex-col gap-2 animate-pulse">
            <div v-for="() in 4" class="bg-accent rounded h-12"></div>
            <div v-for="() in 4" class="bg-accent rounded h-4"></div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useTeams, useCities, useVenues, useTournaments, useEventsSearch } from "@xs2event/js-lib/api";
import { RouterLink } from "vue-router";
import { todayISODate } from "@/helpers/format";
import { EventCard } from "@/components/event";
import { camelToSnake, snakeToCamel } from "@/helpers/casing";
import brandconfig from "@/brandconfig";
import SearchIcon from "~icons/ic/baseline-search";
import CancelIcon from "~icons/ic/baseline-cancel";
import ApartmentIcon from "~icons/ic/baseline-apartment";
import EmojiEventsIcon from "~icons/ic/baseline-emoji-events";
import StadiumIcon from "~icons/ic/baseline-stadium";
import Apparel from "~icons/material-symbols/apparel-sharp";
import { useRoute } from "vue-router";
import { useEventListener } from "@vueuse/core";
import { lookupSportTypeName } from "@/store/sportTypes";
import { eventsWithCity, eventsWithTeam, eventsWithTournament, eventsWithVenue } from "@/constants/routes";

const hasFocus = ref(false);
const { t } = useI18n();

const route = useRoute();

watch(
  () => ({ ...route }),
  () => {
    query.value = "";
    hasFocus.value = false;
  },
  { deep: true }
);

useEventListener("keydown", (e: KeyboardEvent) => {
  if (!hasFocus.value) return;

  if (e.key === "Escape") {
    close();
  }
});

const close = () => {
  (document.activeElement as HTMLElement)?.blur();
  hasFocus.value = false;
  query.value = "";
};

const filterIcons = {
  city: ApartmentIcon,
  team: Apparel,
  tournament: EmojiEventsIcon,
  venue: StadiumIcon,
} as const;

const search = snakeToCamel(brandconfig.search);

const query = ref<string>("");

const isLoading = computed(() => {
  return (
    teamsQuery.isLoading.value ||
    citiesQuery.isLoading.value ||
    tournamentsQuery.isLoading.value ||
    venuesQuery.isLoading.value ||
    eventsQuery.isLoading.value
  );
});

const teamsQuery = useTeams(
  computed(() => ({
    teamName: (query.value && `ilike:${query.value}`) || undefined,
    sorting: "team_name:asc",
    pageSize: 5,
    eventStartdate: `ge:${new Date().toISOString().split("T")[0]}`,
    ...search.params,
    ...search.teams.params,
  })),
  { enabled: computed(() => !!query.value) }
);

const citiesQuery = useCities(
  computed(() => ({ query: (query.value && `ilike:${query.value}`) || undefined, pageSize: 5 })),
  { enabled: computed(() => !!query.value) }
);

const tournamentsQuery = useTournaments(
  computed(() => ({
    tournamentName: (query.value && `ilike:${query.value}`) || undefined,
    dateStop: "ge:" + todayISODate,
    sorting: "tournament_name:asc",
    pageSize: 5,
    ...search.params,
    ...search.teams.params,
  })),
  { enabled: computed(() => !!query.value) }
);

const venuesQuery = useVenues(
  computed(() => ({
    venueName: (query.value && `ilike:${query.value}`) || undefined,
    pageSize: 5,
    eventStopdate: "ge:" + todayISODate,
    sorting: "official_name:asc",
    ...search.params,
    ...search.teams.params,
  })),
  { enabled: computed(() => !!query.value) }
);

const eventsQuery = useEventsSearch(
  computed(() => ({
    query: query.value,
    pageSize: 5,
  })),
  { enabled: computed(() => !!query.value) }
);

const events = computed(() => eventsQuery.data.value?.events || []);

const highlightQuery = (text: string) => {
  if (!query.value) return text;
  const escaped = query.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(escaped, "gi");

  return text.replace(regex, (match) => `<span class="font-bold">${match}</span>`);
};
const otherResults = computed(() => {
  const results = [
    ...(teamsQuery.data.value?.teams?.map((x) => {
      return {
        type: "team",
        name: `${x.officialName}`,
        sportType: `${t(lookupSportTypeName(x.sportType))}`,
        icon: filterIcons.team,
        to: eventsWithTeam(x.slug.toLowerCase(), { sport_type: x.sportType == "soccer" ? "football" : x.sportType }),
      };
    }) || []),
    ...(venuesQuery.data.value?.venues?.map((x) => ({
      type: "venue",
      name: x.officialName,
      icon: filterIcons.venue,
      to: eventsWithVenue(x.slug.toLowerCase()),
    })) || []),
    ...(citiesQuery.data.value?.cities?.map((x) => ({
      type: "city",
      name: x.city,
      icon: filterIcons.city,
      to: eventsWithCity(x.city.toLowerCase()),
    })) || []),
    ...(tournamentsQuery.data.value?.map((x) => ({
      type: "tournament",
      name: `${x.officialName} (${x.season})`,
      icon: filterIcons.tournament,
      to: eventsWithTournament(x.slug.toLowerCase()),
    })) || []),
  ];
  const typeOrder = { team: 0, venue: 1, city: 2, tournament: 3 };
  results.sort((a, b) => {
    if (typeOrder[a.type] !== typeOrder[b.type]) {
      return typeOrder[a.type] - typeOrder[b.type];
    }
    return a.name.localeCompare(b.name);
  });
  return results.slice(0, 5);
});

function isTeamResult(result: OtherResult): result is TeamResult {
  return result.type === "team" && "sportType" in result;
}
</script>

<style scoped>
.search-results {
  max-height: calc(100svh - var(--spacing) * 48);
  overflow: auto;
}
</style>
